import React from "react";
import { Box, Button, Dialog, DialogContent, useMediaQuery } from "@mui/material";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { useNavigate } from "react-router-dom";
import useTranslation from "../../../shared/hooks/useTranslation";
import { dt } from "../../../shared/themes/redesignTokens";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";

interface RequestNoLongerAcceptingDialogProps {
  open: boolean;
  onClose: () => void;
  showSaveAsDraft?: boolean;
  onSaveAsDraft?: () => void;
}

const RequestNoLongerAcceptingDialog = ({
  open,
  onClose,
  showSaveAsDraft,
  onSaveAsDraft,
}: RequestNoLongerAcceptingDialogProps) => {
  const { translate } = useTranslation(["serviceOffers", "common"]);
  const navigate = useNavigate();
  const isPhone = useMediaQuery("(max-width:600px)");

  useRedesignSetup();

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        // No backdrop dismiss, no escape dismiss
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose();
      }}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: dt.radius,
          overflow: "hidden",
          animation: "slideUp 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
          "@keyframes slideUp": {
            "0%": { opacity: 0, transform: "translateY(20px)" },
            "100%": { opacity: 1, transform: "translateY(0)" },
          },
        },
      }}
    >
      {/* Amber gradient accent bar */}
      <Box
        sx={{
          height: "5px",
          background: "linear-gradient(90deg, #F59E0B 0%, #D97706 50%, #B45309 100%)",
        }}
      />

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          px: isPhone ? 3 : 4,
          py: 4,
          gap: 2,
        }}
      >
        {/* Warning icon in circle */}
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: "rgba(245, 158, 11, 0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            animation: "iconPulse 2s ease-in-out infinite",
            "@keyframes iconPulse": {
              "0%, 100%": { transform: "scale(1)" },
              "50%": { transform: "scale(1.06)" },
            },
          }}
        >
          <WarningAmberOutlinedIcon sx={{ fontSize: 32, color: "#D97706" }} />
        </Box>

        {/* Title */}
        <Box
          sx={{
            fontFamily: dt.font,
            fontSize: "20px",
            fontWeight: 700,
            color: dt.textPrimary,
            letterSpacing: "-0.01em",
          }}
        >
          {translate("raceCondition.noLongerAcceptingTitle")}
        </Box>

        {/* Body */}
        <Box
          sx={{
            fontFamily: dt.font,
            fontSize: "14px",
            fontWeight: 400,
            color: dt.textSecondary,
            lineHeight: 1.6,
            maxWidth: "380px",
          }}
        >
          {translate("raceCondition.noLongerAcceptingBody")}
        </Box>

        {/* Hint box */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            background: "#FFFBEB",
            borderLeft: "3px solid #F59E0B",
            borderRadius: "6px",
            padding: "12px 14px",
            width: "100%",
            mt: 1,
          }}
        >
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              fontWeight: 400,
              color: "#92400E",
              lineHeight: 1.5,
              textAlign: "left",
            }}
          >
            {translate("raceCondition.noLongerAcceptingHint")}
          </Box>
        </Box>

        {/* Actions */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isPhone ? "column-reverse" : "row",
            gap: 1.5,
            width: "100%",
            mt: 1,
            justifyContent: "center",
          }}
        >
          {showSaveAsDraft && onSaveAsDraft && (
            <Button
              variant="outlined"
              onClick={() => {
                onSaveAsDraft();
                onClose();
              }}
              sx={{
                fontFamily: dt.font,
                fontWeight: 600,
                fontSize: "14px",
                textTransform: "none",
                borderRadius: "8px",
                px: 3,
                py: 1,
                color: dt.textSecondary,
                borderColor: dt.cardBorder,
                ...(isPhone && { width: "100%" }),
                "&:hover": {
                  borderColor: dt.textSecondary,
                  backgroundColor: "rgba(107, 114, 128, 0.04)",
                },
              }}
            >
              {translate("saveAsDraft")}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={() => {
              onClose();
              navigate("/service-board");
            }}
            sx={{
              fontFamily: dt.font,
              fontWeight: 600,
              fontSize: "14px",
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              py: 1,
              backgroundColor: "#D97706",
              ...(isPhone && { width: "100%" }),
              "&:hover": { backgroundColor: "#B45309" },
            }}
          >
            {translate("raceCondition.returnToServiceBoard")}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default RequestNoLongerAcceptingDialog;
